import { cn } from "@/lib/utils";
import { useCallback, useEffect, useId, useRef } from "react";
import { useEventCallback } from "../hooks/use-event-callback";

const THUMB_INSET = 9;

function calculateThumbX(value: number, min: number, max: number) {
  if (min === max) {
    return 0;
  }

  const thumbX = ((value - min) / (max - min)) * 100;

  return Math.max(0, Math.min(100, thumbX));
}

function calculateValueFromClientX({
  clientX,
  trackElement,
  min,
  max,
  step,
}: {
  clientX: number;
  trackElement: HTMLDivElement;
  min: number;
  max: number;
  step: number;
}) {
  const { left, width } = trackElement.getBoundingClientRect();

  let valuePercentage = 0;

  if (width > 2 * THUMB_INSET) {
    valuePercentage =
      (clientX - left - THUMB_INSET) / (width - 2 * THUMB_INSET);
  }

  const value = min + (max - min) * valuePercentage;

  return roundToStep({ value, step, min, max });
}

function roundToStep({
  value,
  step,
  min,
  max,
}: {
  value: number;
  step: number;
  min: number;
  max: number;
}) {
  const rounded = Math.round(value / step) * step;
  const capped = Math.max(min, Math.min(max, rounded));

  const stepDecimals = (step.toString().split(".")[1] || "").length;
  const decimals = Math.max(1, stepDecimals);

  return Number(capped.toFixed(decimals));
}

type Props = {
  value: number;
  min: number;
  max: number;
  step: number;
  onValueChange?: (value: number) => void;
  onValueCommit?: (value: number) => void;
  classNameTrack?: string;
  classNameTrackActive?: string;
  classNameThumb?: string;
};

export function Slider({
  value,
  min,
  max,
  step,
  onValueChange,
  onValueCommit,
  classNameTrack,
  classNameTrackActive,
  classNameThumb,
}: Props) {
  const thumbId = useId();
  const thumbX = calculateThumbX(value, min, max);
  const trackElement = useRef<HTMLDivElement | null>(null);
  const grabOffsetX = useRef(0);

  const onPointerMove = useEventCallback((e: PointerEvent) => {
    if (!trackElement.current) {
      return;
    }

    const value = calculateValueFromClientX({
      clientX: e.clientX - grabOffsetX.current,
      trackElement: trackElement.current,
      min,
      max,
      step,
    });
    onValueChange?.(value);
  });

  const onPointerUp = useEventCallback((e: PointerEvent) => {
    document.removeEventListener("pointermove", onPointerMove);

    if (!trackElement.current) {
      return;
    }

    const value = calculateValueFromClientX({
      clientX: e.clientX - grabOffsetX.current,
      trackElement: trackElement.current,
      min,
      max,
      step,
    });
    onValueCommit?.(value);
  });

  const onPointerDown = useEventCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!(e.target instanceof HTMLElement) || !trackElement.current) {
        return;
      }

      const thumbElement = document.getElementById(thumbId);
      const isThumbTarget =
        e.target.id === thumbId || thumbElement?.contains(e.target);

      if (isThumbTarget && thumbElement) {
        const { left, width } = thumbElement.getBoundingClientRect();
        grabOffsetX.current = e.clientX - (left + width / 2);
      } else {
        grabOffsetX.current = 0;
        const value = calculateValueFromClientX({
          clientX: e.clientX,
          trackElement: trackElement.current,
          min,
          max,
          step,
        });
        onValueChange?.(value);
      }

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp, { once: true });
    },
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      let newValue: number | null = null;

      switch (e.key) {
        case "ArrowUp":
        case "ArrowRight":
          e.preventDefault();
          newValue = Math.min(max, value + step);
          break;
        case "ArrowDown":
        case "ArrowLeft":
          e.preventDefault();
          newValue = Math.max(min, value - step);
          break;
      }

      if (newValue !== null && newValue !== value) {
        onValueChange?.(newValue);
        onValueCommit?.(newValue);
      }
    },
    [value, min, max, step, onValueChange, onValueCommit],
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  if (min > max || step <= 0) {
    console.error(
      `Slider: min must be less than max, step must be greater than 0, min: ${min}, max: ${max}, step: ${step}`,
    );
    return null;
  }

  return (
    <div
      ref={trackElement}
      onPointerDown={onPointerDown}
      className={cn(
        "relative h-1.5 w-full cursor-pointer rounded-full bg-secondary drag-none select-none",
        classNameTrack,
      )}
    >
      <div
        className={cn(
          "absolute h-full rounded-full bg-linear-to-r from-primary-400 to-secondary-400",
          classNameTrackActive,
        )}
        style={{ width: `calc(${thumbX}% + ${9 - thumbX * 0.18}px)` }}
      />
      <button
        id={thumbId}
        type="button"
        role="slider"
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={cn(
          "absolute top-1/2 h-5 w-5 -translate-y-1/2 transform rounded-full border-2 border-white bg-linear-to-r from-primary-400 to-secondary-400 outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2",
          classNameThumb,
        )}
        style={{ left: `calc(${thumbX}% - ${thumbX * 0.18 + 1}px)` }}
      />
    </div>
  );
}
